import { WebsocketMessage } from '@buyerflo/core/schemas/websocket'
import { useAuth } from '@clerk/react'
import { ClerkOfflineError } from '@clerk/react/errors'
import { useOrgId } from 'helpers'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import * as reactUseWebsocket from 'react-use-websocket'

const GeneralWebsocketContext = createContext<WebsocketMessage | null>(null)

export const GeneralWebsocketProvider = ({ children }) => {
    const orgId = useOrgId()
    const { getToken, isSignedIn } = useAuth()

    const getUrl = useCallback(async (): Promise<string> => {
        let freshToken: string | null = null
        try {
            freshToken = await getToken()
        } catch (err) {
            if (ClerkOfflineError.is(err)) return ''
            throw err
        }
        return `${import.meta.env.VITE_APP_WEBSOCKET_URL}?orgId=${orgId}&token=${freshToken}`
    }, [getToken, orgId])

    const { lastMessage } = reactUseWebsocket.default(isSignedIn ? getUrl : null, {
        heartbeat: true,
        shouldReconnect: () => true,
        reconnectAttempts: 20,
        reconnectInterval: (attempt) => Math.min(1000 * 2 ** attempt, 30000),
    })

    const [newMessage, setNewMessage] = useState<WebsocketMessage | null>(null)

    useEffect(() => {
        if (lastMessage) {
            try {
                const data = JSON.parse(lastMessage.data)
                setNewMessage(data)
            } catch {
                // Ignore malformed messages
            }
        }
    }, [lastMessage])

    return <GeneralWebsocketContext.Provider value={newMessage}>{children}</GeneralWebsocketContext.Provider>
}

const useGeneralWebSocket = () => useContext(GeneralWebsocketContext)

export function useWebSocket(type: WebsocketMessage['type'] | undefined) {
    const [newMessage, setNewMessage] = useState<WebsocketMessage['payload'] | null>(null)

    const lastMessage = useGeneralWebSocket()

    useEffect(() => {
        if (lastMessage) {
            if (lastMessage.type == type) {
                setNewMessage(lastMessage.payload)
            }
        }
    }, [lastMessage])

    return newMessage
}
